import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api, formatVnd, type DeliveredItem, type Order } from '../lib/api'
import { formatDateTime } from './AccountPage'

const ZALO = 'https://zalo.me/0935396912'

function embedUrl(link: string) {
  try {
    const u = new URL(link)
    const id = u.hostname.includes('youtu.be')
      ? u.pathname.slice(1)
      : u.searchParams.get('v') || u.pathname.split('/embed/')[1]
    return id ? `https://www.youtube.com/embed/${id}` : null
  } catch {
    return null
  }
}

function statusText(s: string) {
  if (s === 'cho_giao_hang') return 'đang giao'
  if (s === 'huy_bo' || s === 'da_huy') return 'đã hủy'
  if (s === 'da_giao_hang' || s === 'da_thanh_toan') return 'thành công'
  if (s === 'chua_thanh_toan') return 'chưa thanh toán'
  if (s === 'da_hoan_tien') return 'đã hoàn tiền'
  return s
}

function KeyRow({ item }: { item: DeliveredItem }) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const text = item.key || item.item_preview

  async function copy() {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  if (!open) {
    return (
      <button type="button" className="okey-reveal" onClick={() => setOpen(true)}>
        <i className="fa-solid fa-eye" aria-hidden /> Nhấn để hiện key
      </button>
    )
  }
  return (
    <div className="okey-box">
      <div className="okey-value">{text}</div>
      <button type="button" className="btn btn-primary okey-copy" onClick={copy}>
        <i className={`fa-solid ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden />{' '}
        {copied ? 'Đã copy' : 'Copy'}
      </button>
    </div>
  )
}

function ReviewForm({ orderId, onDone }: { orderId: number; onDone: () => void }) {
  const [stars, setStars] = useState(5)
  const [content, setContent] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr('')
    try {
      await api.reviewOrder(orderId, { stars, content })
      onDone()
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Lỗi gửi')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="okey-review" onSubmit={submit}>
      <div className="okey-stars" role="radiogroup" aria-label="Số sao">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={stars === n}
            aria-label={`${n} sao`}
            className={n <= stars ? 'on' : ''}
            onClick={() => setStars(n)}
          >
            <i className="fa-solid fa-star" aria-hidden />
          </button>
        ))}
      </div>
      <textarea
        rows={3}
        placeholder="Nhận xét"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        minLength={10}
        maxLength={2000}
        required
      />
      {err ? <p className="note">{err}</p> : null}
      <button className="btn btn-primary" type="submit" disabled={busy}>
        <i className="fa-solid fa-paper-plane" aria-hidden /> {busy ? 'Đang gửi…' : 'Gửi'}
      </button>
    </form>
  )
}

export function OrderKeyPage() {
  const { id } = useParams()
  const { user, loading } = useAuth()
  const [order, setOrder] = useState<Order | null>(null)
  const [err, setErr] = useState('')
  const [reviewSent, setReviewSent] = useState(false)

  useEffect(() => {
    if (!user || !id) return
    let alive = true
    let timer: ReturnType<typeof setTimeout>
    const load = () =>
      api
        .order(Number(id))
        .then((r) => {
          if (!alive) return
          setOrder(r.order)
          if (r.order.status === 'cho_giao_hang') timer = setTimeout(load, 3000)
        })
        .catch((e) => alive && setErr(e.message))
    load()
    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [user, id])

  if (loading) return <p className="muted">Đang tải…</p>
  if (!user) return <Navigate to="/login" replace />
  if (err) return <p className="note">{err}</p>
  if (!order) return <p className="muted">Đang tải…</p>
  if (order.status === 'chua_thanh_toan') return <Navigate to={`/checkout/${order.id}`} replace />

  const cancelled = order.status === 'huy_bo' || order.status === 'da_huy'
  const payLabel = order.pay_method === 'bank' ? 'CK' : order.pay_method === 'xu' ? 'Xu' : order.pay_method === 'gem' ? 'Gem' : null

  return (
    <section className="okey">
      <Link className="okey-back" to="/account">
        <i className="fa-solid fa-arrow-left" aria-hidden /> Đơn hàng của tôi
      </Link>

      <div className="okey-card">
        <h1>Key của bạn</h1>
        <p className="okey-sub">{order.name || 'Đơn hàng'}</p>

        {order.status === 'cho_giao_hang' ? (
          <div className="okey-wait">
            <i className="fa-solid fa-spinner fa-spin" aria-hidden />
            <p>Đang giao key…</p>
          </div>
        ) : cancelled ? (
          <p className="okey-alert okey-alert-danger">Đơn đã hủy</p>
        ) : order.status === 'da_giao_hang' && order.deliver_note ? (
          <div
            className="okey-alert okey-alert-ok"
            dangerouslySetInnerHTML={{ __html: order.deliver_note }}
          />
        ) : order.items?.length ? (
          <div className="okey-list">
            {order.items.map((it) => (
              <KeyRow key={it.id} item={it} />
            ))}
          </div>
        ) : (
          <p className="okey-alert okey-alert-warn">Chưa có key — thử tải lại trang.</p>
        )}

        {order.replace_items?.length ? (
          <div className="okey-replace">
            <p>Key đổi</p>
            <div className="okey-list">
              {order.replace_items.map((it) => (
                <KeyRow key={it.id} item={it} />
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <details className="okey-meta">
        <summary>
          <i className="fa-solid fa-receipt" aria-hidden /> Chi tiết đơn
        </summary>
        <div className="okey-meta-body">
          <p>Trạng thái: {statusText(order.status)}</p>
          <p>
            Mã: <b>{order.trans_id}</b>
          </p>
          <p>Ngày: {formatDateTime(order.created_at)}</p>
          <p>
            SL: {order.quantity} · {formatVnd(order.total)}
            {payLabel ? ` · ${payLabel}` : ''}
          </p>
          {order.admin_note ? (
            <p>
              <small>{order.admin_note}</small>
            </p>
          ) : null}
        </div>
      </details>

      {order.is_minecraft ? (
        <details className="okey-meta">
          <summary>
            <i className="fa-solid fa-book-open" aria-hidden /> Hướng dẫn
          </summary>
          <div className="okey-meta-body">
            <p>
              <a
                href="https://accminecraft.net/bai-viet/huong-dan-doi-thong-tin-tai-khoan-nhanh-bang-recovery-code-mqapvxst"
                target="_blank"
                rel="noreferrer"
              >
                Đổi thông tin tài khoản
              </a>
            </p>
            <p>
              <a href="https://www.youtube.com/watch?v=1RhWMMjUX6I" target="_blank" rel="noreferrer">
                Video YouTube
              </a>
            </p>
          </div>
        </details>
      ) : null}

      {order.videos?.length ? (
        <details className="okey-meta">
          <summary>
            <i className="fa-brands fa-youtube" aria-hidden /> Video hướng dẫn
          </summary>
          <div className="okey-meta-body">
            {order.videos.map((v) => {
              const src = embedUrl(v)
              return src ? (
                <div key={v} className="okey-video">
                  <iframe src={src} title="YouTube" allowFullScreen loading="lazy" />
                </div>
              ) : null
            })}
          </div>
        </details>
      ) : null}

      {order.guide_blog ? (
        <details className="okey-meta">
          <summary>
            <i className="fa-solid fa-newspaper" aria-hidden /> Bài hướng dẫn
          </summary>
          <div className="okey-meta-body">
            <Link to={`/blog/${order.guide_blog.uri}`}>{order.guide_blog.title}</Link>
          </div>
        </details>
      ) : null}

      <div className="okey-side">
        <h2>Hỗ trợ</h2>
        <a className="okey-zalo" href={ZALO} target="_blank" rel="noreferrer">
          <i className="fa-solid fa-comment-dots" aria-hidden /> Zalo 0935396912
        </a>
      </div>

      {order.can_review || order.reviewed || reviewSent ? (
        <div className="okey-side">
          {order.reviewed || reviewSent ? (
            <p className="muted okey-done">
              <i className="fa-solid fa-circle-check" aria-hidden />{' '}
              {reviewSent ? 'Cảm ơn bạn đã đánh giá!' : 'Đã đánh giá.'}
            </p>
          ) : (
            <>
              <h2>Đánh giá</h2>
              <ReviewForm orderId={order.id} onDone={() => setReviewSent(true)} />
            </>
          )}
        </div>
      ) : null}
    </section>
  )
}
